import { useState, useCallback, useMemo, useEffect } from 'react'
import { authAPI } from '../api'
import { clearAuthSessionStorage } from '../utils/authSessionCleanup'
import { parseStoredUser } from '../utils/authSession'
import { SUBSCRIPTION_EXPIRED_EVENT, SUBSCRIPTION_ACTIVATED_EVENT } from '../utils/permissionDeniedEvents'
import {
  buildAuthorizedNavigation,
  getActiveMenu as resolveActiveMenu,
  getActiveSubmenu as resolveActiveSubmenu
} from '../config/navigation'

import { checkModuleEntitlement } from '../utils/moduleAccess'

/**
 * Custom hook for authentication and authorization.
 * Manages login, logout, token validation, RBAC privilege checks, and dynamic authorized menus.
 */
export function useAuth() {
  const [error, setError] = useState(null)

  const userContext = useMemo(() => parseStoredUser(), [])

  const [sessionModules, setSessionModules] = useState(
    () => userContext.modules || []
  )

  useEffect(() => {
    function onSubscriptionExpired(event) {
      const expiredModule = String(event?.detail?.module || '').toLowerCase()
      if (!expiredModule) return
      setSessionModules((prev) => prev.filter((m) => m !== expiredModule))
    }

    function onSubscriptionActivated(event) {
      const newModules = event?.detail?.modules
      if (Array.isArray(newModules) && newModules.length > 0) {
        setSessionModules(newModules)
        try {
          const raw = localStorage.getItem('user')
          if (raw) {
            const stored = JSON.parse(raw)
            stored.modules = newModules
            localStorage.setItem('user', JSON.stringify(stored))
          }
        } catch (_) {}
      }
    }

    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, onSubscriptionExpired)
    window.addEventListener(SUBSCRIPTION_ACTIVATED_EVENT, onSubscriptionActivated)
    return () => {
      window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, onSubscriptionExpired)
      window.removeEventListener(SUBSCRIPTION_ACTIVATED_EVENT, onSubscriptionActivated)
    }
  }, [])

  const effectiveContext = useMemo(() => ({
    ...userContext,
    modules: sessionModules,
  }), [userContext, sessionModules])

  const menus = useMemo(() => {
    return buildAuthorizedNavigation(effectiveContext)
  }, [effectiveContext])

  const getActiveMenu = useCallback((pathname) => {
    return resolveActiveMenu(pathname, menus)
  }, [menus])

  const getActiveSubmenu = useCallback((pathname) => {
    return resolveActiveSubmenu(pathname, menus)
  }, [menus])

  const isAuthenticated = useCallback(async () => {
    const token = localStorage.getItem('authToken')
    return !!token
  }, [])

  const login = useCallback(async (credentials) => {
    try {
      setError(null)
      const response = await authAPI.login(credentials)

      // Store token
      const token = response.token || response.access_token
      if (token) {
        localStorage.setItem('authToken', token)
      }

      return response
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Login failed'
      setError(errorMsg)
      throw err
    }
  }, [])

  const logout = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('auth:logout'))
    } catch (_) {}
    clearAuthSessionStorage()
    localStorage.removeItem('rememberMe')
    localStorage.removeItem('rememberEmail')
    setError(null)
  }, [])

  const getToken = useCallback(() => {
    return localStorage.getItem('authToken')
  }, [])

  /**
   * Check if the authenticated user has a specific privilege.
   * Admins automatically bypass and have all privileges.
   */
  const hasPrivilege = useCallback(
    (privilegeName) => {
      if (!privilegeName) return false
      if (effectiveContext.isAdmin) return true
      return effectiveContext.privileges.includes(privilegeName)
    },
    [effectiveContext]
  )

  /**
   * Check if the authenticated user has any of the specified privileges.
   */
  const hasAnyPrivilege = useCallback(
    (privilegeList = []) => {
      if (effectiveContext.isAdmin) return true
      const granted = new Set(effectiveContext.privileges)
      return privilegeList.some((p) => granted.has(p))
    },
    [effectiveContext]
  )

  const hasModule = useCallback(
    (mod) => checkModuleEntitlement(mod, sessionModules),
    [sessionModules]
  )

  return {
    user: effectiveContext,
    userId: effectiveContext.userId || null,
    login,
    logout,
    getToken,
    isAuthenticated,
    error,
    setError,
    isAdmin: effectiveContext.isAdmin,
    privileges: effectiveContext.privileges,
    modules: sessionModules,
    hasPrivilege,
    hasAnyPrivilege,
    hasModule,
    menus,
    getActiveMenu,
    getActiveSubmenu,
  }
}
